import { useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { token } from "@atlaskit/tokens";

import { Button } from "@/libraries/design/components/button/src/button";
import { wacToken } from "@/libraries/design/foundations";

import { Picture } from "../../components/wpl/image/src/image";

import { SourcetreeAppDisclaimer } from "./sourcetree-app-disclaimer";

const stickyWrapperStyle = css({
    lineHeight: 1,
    boxSizing: "border-box",
    margin: 0,
    padding: 0,
    border: 0,
    fontSize: "100%",
    verticalAlign: "baseline",
    order: -1
});

const imktNavbarStyle = css({
    lineHeight: 1,
    boxSizing: "inherit",
    margin: 0,
    padding: 0,
    border: 0,
    fontSize: "100%",
    verticalAlign: "baseline",
    display: "block",
    order: -1,
    width: "100%"
});

const containerFluidStyle = css({
    lineHeight: 1,
    boxSizing: "border-box",
    margin: 0,
    padding: 0,
    border: 0,
    fontSize: "100%",
    verticalAlign: "baseline",
    backgroundColor: wacToken("color.neutral.light0"),
    boxShadow: token("elevation.shadow.overlay"),
    minWidth: "100%",
    clear: "both",
    position: "relative",
    maxHeight: "140px",
    overflow: "hidden",
    zIndex: 9,
    minHeight: "72px"
});

export const GLOBAL_NAV_HEIGHT = "68px";

const navigationBarWrapperCss = css({
    alignItems: "center",
    background: "white",
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    zIndex: 5,
    height: GLOBAL_NAV_HEIGHT
});

const navWrapper = css({
    "display": "flex",
    "alignItems": "center",
    "justifyContent": "center",
    "flex": 1,
    "height": "100%",
    "flexDirection": "row",
    "zIndex": 6,
    "marginInline": "24px",

    "@media (min-width: 768px)": {
        maxWidth: "1152px",
        paddingInline: "0",
        marginInline: "60px"
    }
});

const navbarStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    height: "100%"
});

const navigationBarLogoCss = css({
    "display": "flex",
    "alignItems": "center",
    "@media (min-width: 768px)": {
        width: "auto"
    }
});

const navigationBarLogoLink = css({
    display: "flex"
});

const logoStyle = css({
    "lineHeight": 1,
    "boxSizing": "border-box",
    "margin": 0,
    "padding": 0,
    "border": 0,
    "fontSize": "100%",
    "color": wacToken("color.primary.blue"),
    "height": 30,
    "maxWidth": "100%",
    "pointerEvents": "none",

    "@media (max-width: 1024px)": {
        height: 26
    },

    "@media (max-width: 680px)": {
        height: 22
    }
});

const buttonWrapperCss = css({
    "display": "flex",

    "@media (max-width: 680px)": {
        display: "none"
    }
});

const DOWNLOAD_URL =
    "https://product-downloads.atlassian.com/software/sourcetree/ga/Sourcetree_4.2.18_313.zip";

export const SourcetreeAppNav = () => {
    const intl = useIntl();
    const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false);

    const buttonText = intl.formatMessage({
        id: "sourcetreeapp.globalnav.button.download.free",
        defaultMessage: "Download free"
    });

    const handleDownloadButtonClick = (
        event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
    ) => {
        // prevent immediate navigation to the zip
        event.preventDefault();
        // open the disclaimer modal
        setIsDisclaimerOpen(true);
    };

    const handleDisclaimerClose = () => {
        setIsDisclaimerOpen(false);
    };

    const handleDisclaimerConfirm = () => {
        // user accepted: close modal
        setIsDisclaimerOpen(false);
        // then trigger the real download
        window.location.href = DOWNLOAD_URL;
    };

    return (
        <>
            <div css={stickyWrapperStyle}>
                <nav css={imktNavbarStyle}>
                    <div css={containerFluidStyle}>
                        <div css={navigationBarWrapperCss}>
                            <div css={navWrapper}>
                                <div css={navbarStyle}>
                                    <div css={navigationBarLogoCss}>
                                        <a href="/" css={navigationBarLogoLink}>
                                            <Picture
                                                image={{
                                                    src: "https://wac-cdn.atlassian.com/dam/jcr:f32681c1-355d-4806-b29c-319b0c6ecb06/Sourcetree-blue.svg?cdnVersion=3108",
                                                    alt: "Sourcetree logo"
                                                }}
                                                css={logoStyle}
                                            />
                                        </a>
                                    </div>

                                    <div css={buttonWrapperCss}>
                                        <Button
                                            variant="primary"
                                            size="small"
                                            color="blue"
                                            href={DOWNLOAD_URL}
                                            onClick={handleDownloadButtonClick}
                                            dataEventProps={{
                                                "data-event": "clicked",
                                                "data-event-component":
                                                    "linkButton",
                                                "data-event-container":
                                                    "navbar",
                                                "data-product-key":
                                                    "sourcetreeapp",
                                                "data-label-english":
                                                    "Download free"
                                            }}
                                        >
                                            {buttonText}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </nav>
            </div>

            <SourcetreeAppDisclaimer
                isOpen={isDisclaimerOpen}
                onClose={handleDisclaimerClose}
                onConfirm={handleDisclaimerConfirm}
            />
        </>
    );
};
